import { Injectable, OnDestroy } from '@angular/core';
import oneSchemaImporter, {
    OneSchemaImporterClass,
    OneSchemaLaunchStatus
} from '@oneschema/importer';
import {
    BehaviorSubject,
    catchError,
    Observable,
    of,
    Subject,
    takeUntil
} from 'rxjs';
import { PI } from '../../constants-types';
import {
    InitDataService,
    UserProfileSettingsService
} from '../../system-services';
import { DataImportRepositoryService } from '../data';
import { OneSchemaResponse } from '../interfaces';
import { OneSchemaUserJwtClaims } from '../interfaces/one-schema-user-jwt-claims';

@Injectable({
    providedIn: 'root'
})
export class OneSchemaService implements OnDestroy {
    /**
     * Direct access to the initialized OneSchema
     * importer class.
     */
    importer: OneSchemaImporterClass;

    /**
     * Launched event
     *
     * Emitted when OneSchema opens its
     * iframe modal.
     */
    launched$: Observable<void>;

    /**
     * Success event
     *
     * Emitted when OneSchema emits a
     * success event from the iframe.
     *
     * Returns a OneSchemaResponse observable
     * object to be processed by a backend
     * persistence layer.
     */
    success$: Observable<OneSchemaResponse>;

    /**
     * Cancel event
     *
     * Emitted when a user closes the
     * OneSchema iframe modal.
     */
    cancel$: Observable<void>;

    /**
     * Error event
     *
     * Emitted when an error occurs during
     * the OneSchema upload.
     *
     * Returns a string observable message
     * representing the error.
     */
    error$: Observable<string>;

    /**
     * Internal error event
     *
     * Emitted when an error occurs while obtaining
     * the prerequisites to open the OneSchema iframe.
     *
     * Returns a string observable message
     * representing the error.
     */
    internalError$: Observable<string>;

    private userJwt: string;

    private ready = new BehaviorSubject(false);
    private launched = new Subject<void>();
    private success = new Subject<OneSchemaResponse>();
    private cancel = new Subject<void>();
    private error = new Subject<string>();
    private internalError = new Subject<string>();

    private destroyed$ = new Subject<void>();

    constructor(
        private dataImportRepository: DataImportRepositoryService,
        private initData: InitDataService,
        private userProfile: UserProfileSettingsService
    ) {
        this.launched$ = this.launched.asObservable();
        this.success$ = this.success.asObservable();
        this.cancel$ = this.cancel.asObservable();
        this.error$ = this.error.asObservable();
        this.internalError$ = this.internalError.asObservable();

        this.ready.pipe(takeUntil(this.destroyed$)).subscribe((ready) => {
            if (ready) {
                this.registerLaunchedHandler();
                this.registerSuccessHandler();
                this.registerCancelHandler();
                this.registerErrorHandler();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    /**
     * Initializes a new OneSchema importer. Returns a
     * boolean observable true when fully initialized.
     *
     * @param templateKey - Optional template key provided at initialization.
     * @param styles - Optional styles to style the iframe. Default styles provided.
     * @returns Observable boolean, true when service is ready to launch.
     */
    initialize(
        templateKey?: string,
        styles?: Partial<CSSStyleDeclaration>
    ): Observable<boolean> {
        const clientId = PI?.appSettings?.oneSchemaClientId ?? '';

        if (clientId) {
            this.importer = oneSchemaImporter({
                clientId: clientId,
                templateKey,
                // Dev mode is enabled in non-prod environments since cost is tracked based
                // on successful uploads in environments where devMode is off. This helps
                // reduce costs accrued from non-prod environments.
                // Note that devMode does limit uploads to 50 rows.
                devMode: PI.appSettings.environmentName !== 'prod',
                styles: styles ?? {
                    height: '100vh',
                    width: '100vw',
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    zIndex: '10000',
                    border: '0'
                }
            });

            if (!this.userJwt) {
                this.getUserJwt();
            }
        } else {
            this.internalError.next(
                'An error occurred getting the OneSchema Client ID.'
            );
        }

        return this.ready.asObservable();
    }

    /**
     * Launch the OneSchema iframe modal.
     *
     * @param templateKey - required template key to match the OneSchema template.
     * @returns OneSchemaLaunchStatus object.
     */
    launch(templateKey: string): OneSchemaLaunchStatus {
        return this.importer.launch({
            templateKey,
            userJwt: this.userJwt
        });
    }

    /**
     * Update styles of the importer
     *
     * @param styles - Required styles to provide to the OneSchema importer iframe.
     */
    updateModalStyle(styles: Partial<CSSStyleDeclaration>) {
        this.importer.setStyles(styles);
    }

    private registerLaunchedHandler() {
        this.importer.on('launched', () => {
            this.launched.next();
        });
    }

    private registerSuccessHandler() {
        this.importer.on('success', (data: OneSchemaResponse) => {
            data.embed_user_jwt = this.userJwt;
            this.success.next(data);
        });
    }

    private registerCancelHandler() {
        this.importer.on('cancel', () => {
            this.cancel.next();
        });
    }

    private registerErrorHandler() {
        this.importer.on('error', (message: string) => {
            this.error.next(message);
        });
    }

    private getUserJwt() {
        const claims: OneSchemaUserJwtClaims = {
            user_guid: this.initData.user.userGuid,
            // Updated company_guid to used currentCompany instead of initData to account for Third Party Users
            // in an account that isn't their 'home' account
            company_guid: this.userProfile.currentCompany,
            oneschema_client_id: PI?.appSettings?.oneSchemaClientId ?? ''
        };

        this.dataImportRepository
            .fetchUserJwt(claims)
            .pipe(
                takeUntil(this.destroyed$),
                catchError(() => {
                    this.internalError.next(
                        'An error occurred getting the user JWT.'
                    );
                    return of('');
                })
            )
            .subscribe((userJwt) => {
                if (userJwt) {
                    this.userJwt = userJwt;
                    this.ready.next(true);
                } else {
                    this.ready.next(false);
                }
            });
    }
}
